import { ImportDocument } from "../documents"
import { LayerValueType, LayerZoneTypeId } from "../generated/enums"
import { getTextStyles } from "../map/styles/text"
import { isNumber, isUndefined, isUndefinedOrNull } from "../utils/utils"
import { Datalayer, labelMinOpacity } from "./datalayer"
import { IDatalayerKeyItem, IDataLayerZone, IDatalayerZoneMapInstance, IDatalayerZoneUpdate, LayerEditMode, ZoneTextLocation } from "./types"
import { ol_Feature, ol_geom } from "@omnia/exports/openlayers";

export class DatalayerZone implements IDatalayerZoneMapInstance {

    public readonly key: string

    protected _value: any
    protected _label: string
    protected _zoneNumber: number

    protected _area: number
    protected _dimension: { area: number, length: number }

    protected _initGeometry: ol_geom.Geometry
    protected _initWkt: string
    protected _initValue: any
    protected _initArea: number
    protected _initZoneNumber: number

    protected _picture: ImportDocument;

    public zoneName: string;


    constructor(protected _layer: Datalayer, protected _zone: IDataLayerZone, private _feature?: ol_Feature) {

        this.key = `${_layer.datalayerId}:${_zone.DataLayerZoneId}`

        this._value = _zone.Value
        this._initValue = this._value
        this._label = _zone.Label
        this._zoneNumber = _zone.Number
        this._picture = _zone.Document

        this.initialize()

    }


    protected initialize() {
        this._area = isNumber(this._zone.Area) ? this._zone.Area : undefined
        this.zoneName = this.getDefaultZoneName()
    }

    get id(): number {
        return this._zone.DataLayerZoneId;
    }

    get layer() {
        return this._layer;
    }

    get layerId() {
        return this._layer.datalayerId
    }

    get zone() {
        return this._zone;
    }

    get geometry() {
        return this._feature && this.feature.getGeometry()
    }

    get feature(): ol_Feature {
        return this._feature;
    }

    setFeature(feature: ol_Feature, area?: number) {

        this._feature = feature
        this._area = undefined
        this._initArea = undefined
        this._dimension = undefined

        if (feature) {
            feature.set("data", this);
            feature.set("layer", this._layer);
            this._area = this._initArea = isNumber(area) ? area : this.measureArea(feature)
        }

    }

    get inEditor(): boolean {
        return (this._layer.editMode == LayerEditMode.InEditor);
    }

    get editMode(): boolean {
        return (this._layer.editMode == LayerEditMode.EditTarget);
    }

    get picture(): ImportDocument {
        return this._picture;
    }

    set picture(value: ImportDocument) {
        this._picture = value;
    }

    isNew(): boolean {
        return !this._zone.DataLayerZoneId;
    }

    hasDiscreteValues(): boolean {
        return this._layer.hasDiscreteValues();
    }

    getDescription(full: boolean = true): string {

        let d = ""

        if (this.layer.showZoneNumbers()) {
            d = "Zone #" + (this.zoneNumber || "?") + (this.isNew() ? " (new)" : "")
        }

        if (full) {

            d += d ? " - " : ""

            d += Math.round(this.area / 100) / 100 + " ha";

            let v = this.getValueDescription();


            if (v) {
                d += " - " + v;
            }

        }

        return d;

    }

    protected getValueDescription(): string {

        if (this.value) {
            return this.getZoneText(ZoneTextLocation.ZoneDescription)
        }

    }

    get hasFeature(): boolean {
        return !!this._feature
    }

    get currentFeatureArea() {
        return this.measureArea()
    }

    get currentFeatureLength() {
        return this.measureLength()
    }

    get area(): number {

        if (isUndefined(this._area)) {
            if (!this._feature) return null;
            this._area = this.measureArea(this._feature)
        }

        return this._area

    }

    get initialArea(): number {
        return this._initArea
    }

    get storedArea() {
        return this._zone ? this._zone.Area : undefined
    }

    get initialGeometry() {
        return this._initGeometry;
    }

    get hasAreaChanged(): boolean {

        if (this.feature && this.zone && this.zone.DataLayerZoneId) {
            return (this.area != this._initArea)
        }

        return false;

    }

    get valueLabel(): string {
        return this._layer.valueLabel;
    }

    get supportedValues() {
        return this._layer.supportedValues;
    }

    getSupportedValuesKey(): IDatalayerKeyItem[] {
        return this._layer.getFullKey();
    }

    get zoneType(): LayerZoneTypeId {
        return this._zone.TypeId;
    }

    get zoneNumber() {
        return this._zoneNumber
    }

    set zoneNumber(value: number) {
        if (isUndefinedOrNull(value) || (isNumber(value) && (value > 0))) {
            this._zoneNumber = value
        }
    }

    get value(): any {
        return this._value;
    }

    set value(value: any) {
        this.setValue(value);
    }

    setValue(value: any, notify: boolean = true) {

        if (this.layer && this.layer.hasNumericValues()) {
            if (!value) value = 0;
        }

        this._value = value;

        if (notify) {
            this.onValueChanged();
        }

    }

    get label(): string {
        return this._label
    }

    set label(value: string) {
        if (this._label != value) {
            this._label = value
            this.onLabelChanged()
        }
    }

    get showStroke() {
        return true
    }

    isTransparent() {
        return false
    }

    onValueChanged() {

        this._layer.refresh()

        const ctxt = this._layer.context;

        if (ctxt && ctxt.onZoneValueChanged) {
            ctxt.onZoneValueChanged(this);
        }

    }

    protected onLabelChanged() {
        this.refresh()
    }


    commitState() {

        this._initGeometry = null
        this._initWkt = null
        this._initValue = null
        this._initZoneNumber = null

        this._zone.Label = this.label
        this._zone.DocumentId = this._picture ? this._picture.ImportDocumentId : null
        this._area = undefined
        this._dimension = undefined

    }

    setInitialState() {

        if (this._initGeometry) {
            return false
        }

        this._initValue = this._value
        this._initZoneNumber = this.zoneNumber
        this.label = this._zone.Label

        if (this._feature) {
            this._initGeometry = (<any>this._feature.getGeometry()).clone();
            this._initWkt = this._layer.getGeometryAsWKT(this._initGeometry);
        }

        return true

    }

    cancelChanges() {

        if (!this._initGeometry) {
            return false
        }

        this.feature.setGeometry(this._initGeometry)
        this._value = this._initValue
        this.zoneNumber = this._initZoneNumber

        this.label = this._zone.Label
        this.picture = this._zone.Document

        this.commitState()

        return true

    }

    hasChanged(checkGeometry: boolean = true): boolean {

        if (this._initValue != this._value) return true;
        if (this.label != this._zone.Label) return true;

        if ((this._zone.DocumentId && this.picture && this._zone.DocumentId !== this.picture.ImportDocumentId) ||
            (this._zone.DocumentId && !this.picture) ||
            (!this._zone.DocumentId && this.picture)) {
            return true;
        }

        if (!checkGeometry) return false;

        return this.hasGeometryChanged();

    }

    hasGeometryChanged(): boolean {

        if (this._initWkt) {
            return this._initWkt != this._layer.getGeometryAsWKT(this._feature.getGeometry());
        }

    }

    getText(): { text: string, fontSize?: number, bold?: boolean, italic?: boolean }[] {

        let text = this.getZoneText(ZoneTextLocation.MapFeature)

        if (this._layer.editMode == LayerEditMode.EditTarget) {
            text = this.getDescription(false) + (text ? "\n" + text : "")
        }

        return (!text)
            ? []
            : [
                { text, fontSize: 14 }
            ]

    }

    getTextStyles(resolution: number, offsetY: number) {

        const options = { offsetY }

        const styles = getTextStyles(resolution, this.getText(), options)

        return styles

    }


    getZoneText(location: ZoneTextLocation = ZoneTextLocation.MapFeature) {
        return this._layer.getZoneText(this, location)
    }

    shouldShowTooltip() {
        if (this.layer.opacity < labelMinOpacity) return false
        if (this.layer.topmost === false) return false
        let text = this.getZoneText(ZoneTextLocation.MapTooltip);
        if (isUndefinedOrNull(text) || (text === "")) return false;
        return true;
    }

    getZoneTooltip() {
        return this.getZoneText(ZoneTextLocation.MapTooltip);
    }

    selectDiscreteValue(value: any) {
        var type = this._layer.type;
        if (type && type.Values) {
            this.value = type.Values.find(v => v.Value == value);
        }
    }

    getUpdateModel(): IDatalayerZoneUpdate {

        const z = this
        const zone = z.zone
        const type = this.layer.type

        const wkt = z.feature ? this.layer.getGeometryAsWKT(z.feature.getGeometry()) : undefined

        let value = null;

        if (type.ValueType == LayerValueType.Discrete) {
            value = z.value ? z.value.Value : null;
        } else {
            value = z.value;
        }

        return {

            DataLayerZoneId: zone.DataLayerZoneId,

            LayerId: zone.LayerId,
            Id: zone.Id,

            Number: z.zoneNumber,
            TypeId: zone.TypeId,

            Value: value,
            InitValue: z.zone.InitValue,
            Label: z.label,
            DocumentId: z.zone.DocumentId,

            Geometry: wkt,

        }
    }

    refresh() {

        if (this.feature && this.layer.isVisible) {
            const s = this.feature.getStyle()
            this.feature.setStyle(null)
            this.feature.setStyle(s)
        }

    }

    getDefaultZoneName() {
        return this.zone.Name
    }

    resetDimensions() {
        this._area = undefined
        this._dimension = undefined
    }


    measure(geom?: ol_Feature | ol_geom.Geometry) {
        return this._layer.measure(geom || this.feature)
    }

    measureArea(geom?: ol_Feature | ol_geom.Geometry) {
        return this._layer.measureArea(geom || this.feature)
    }

    measureLength(geom?: ol_Feature | ol_geom.Geometry) {
        return this._layer.measureLength(geom || this.feature)
    }

}
